import { ErrorHandler, Injectable, inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { environment } from '@environments/environment';
import { isProductionLike } from '../config/environment-utils';
import { LogEntry, LogLevel } from './logger.service';
import { sanitizeUrl } from '../utils/url-sanitizer';

/**
 * Sentry configuration interface
 */
export interface SentryConfig {
  dsn: string;
  environment: string;
  release?: string;
  tracesSampleRate: number;
  /**
   * Per-event probability (0..1) of an error/message event being sent.
   * Throttles error ingest under bursts (avoids HTTP 429 from the ingest
   * endpoint). Distinct from `tracesSampleRate` (performance only).
   */
  sampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  enabled: boolean;
}

/**
 * Get Sentry configuration from environment
 */
export function getSentryConfig(): SentryConfig {
  return {
    dsn: environment.sentry?.dsn || '',
    environment: environment.production ? 'production' : environment.staging ? 'staging' : 'development',
    release: environment.version ? `razor-admin@${environment.version}` : undefined,
    tracesSampleRate: environment.sentry?.tracesSampleRate ?? (environment.production ? 0.1 : 1.0),
    // Error-event sample rate. Default to a conservative 0.25 (25%) in
    // production to keep error reporting representative while throttling the
    // 100% SDK default that was saturating the ingest quota (HTTP 429); full
    // capture in dev where volume is low.
    sampleRate: environment.sentry?.sampleRate ?? (environment.production ? 0.25 : 1.0),
    replaysSessionSampleRate: environment.sentry?.replaysSessionSampleRate ?? 0,
    replaysOnErrorSampleRate: environment.sentry?.replaysOnErrorSampleRate ?? 0,
    enabled: environment.sentry?.enabled ?? environment.production,
  };
}

// --- PII Scrubbing Utilities (module-level for reuse) ---

/** Comprehensive PII scrubbing patterns for fintech context */
const SENSITIVE_KEY_PATTERNS = [
  // Authentication — use specific terms to avoid matching sortKey, accountType, etc.
  'password', 'passwd', 'secret', 'credential', 'bearer', 'jwt',
  'api_key', 'apikey', 'access_key', 'private_key', 'secret_key',
  'refresh_token', 'access_token', 'auth_token',
  // Financial PII
  'card_number', 'cardnumber', 'cvv', 'cvc', 'routing_number',
  'iban', 'swift', 'ssn', 'social_security',
  // Personal identifiers
  'dob', 'date_of_birth', 'birth_date',
  // Session data
  'cookie', 'csrf', 'csrf_token',
];

/** Value-level PII patterns (regex) for detecting sensitive data in string values */
const SENSITIVE_VALUE_PATTERNS = [
  // JWT tokens (header.payload.signature)
  /eyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+/,
  // Email addresses
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
  // SSN (US)
  /\b\d{3}-\d{2}-\d{4}\b/,
  // Bearer tokens in strings
  /Bearer\s+[A-Za-z0-9._~+/=-]+/i,
];

const MAX_SCRUB_DEPTH = 10;

function isSensitiveKey(keyName: string): boolean {
  const lowerKey = keyName.toLowerCase();
  return SENSITIVE_KEY_PATTERNS.some(pattern => lowerKey.includes(pattern));
}

function containsSensitiveValue(value: string): boolean {
  return SENSITIVE_VALUE_PATTERNS.some(pattern => pattern.test(value));
}

/**
 * Recursively scrub sensitive data from an object.
 * Handles nested objects and arrays. Depth-limited and circular-reference-safe.
 */
function scrubObject(
  obj: Record<string, unknown>,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): Record<string, unknown> {
  if (depth >= MAX_SCRUB_DEPTH) {
    return { '[TRUNCATED]': 'max scrub depth reached' };
  }
  if (seen.has(obj)) {
    return { '[CIRCULAR]': true };
  }
  seen.add(obj);

  const scrubbed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (isSensitiveKey(key)) {
      scrubbed[key] = '[REDACTED]';
    } else if (typeof value === 'string' && containsSensitiveValue(value)) {
      scrubbed[key] = '[REDACTED]';
    } else if (Array.isArray(value)) {
      scrubbed[key] = scrubArray(value, depth + 1, seen);
    } else if (value && typeof value === 'object') {
      scrubbed[key] = scrubObject(value as Record<string, unknown>, depth + 1, seen);
    } else {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
}

/** Recursively scrub arrays, handling nested arrays and objects */
function scrubArray(arr: unknown[], depth: number, seen: WeakSet<object>): unknown[] {
  if (depth >= MAX_SCRUB_DEPTH) return ['[TRUNCATED]'];
  return arr.map(item => {
    if (typeof item === 'string' && containsSensitiveValue(item)) {
      return '[REDACTED]';
    }
    if (Array.isArray(item)) {
      return scrubArray(item, depth + 1, seen);
    }
    if (item && typeof item === 'object') {
      return scrubObject(item as Record<string, unknown>, depth + 1, seen);
    }
    return item;
  });
}

/**
 * Sanitize error type name to prevent leaking internal class names.
 * Maps to generic categories safe for third-party services.
 */
function sanitizeErrorType(error: unknown): string {
  const name = error?.constructor?.name;
  if (!name || name === 'Object') return 'Unknown';

  // Map to safe categories based on common patterns
  const lowerName = name.toLowerCase();
  if (lowerName.includes('http') || lowerName.includes('network') || lowerName.includes('fetch')) return 'NetworkError';
  if (lowerName.includes('auth') || lowerName.includes('permission') || lowerName.includes('forbidden')) return 'AuthError';
  if (lowerName.includes('validation') || lowerName.includes('invalid')) return 'ValidationError';
  if (lowerName.includes('timeout')) return 'TimeoutError';
  if (lowerName.includes('notfound') || lowerName.includes('404')) return 'NotFoundError';
  if (name === 'TypeError') return 'TypeError';
  if (name === 'RangeError') return 'RangeError';
  if (name === 'SyntaxError') return 'SyntaxError';
  if (name === 'ReferenceError') return 'ReferenceError';
  if (name === 'Error') return 'Error';
  return 'ApplicationError';
}

/**
 * Narrow an unknown value to a numeric HTTP status, or null.
 *
 * Reads BOTH `.status` (raw HttpErrorResponse / objects that expose status) and
 * `.statusCode`. The latter is required for the categorized `SchemaServiceError`
 * (ADMIN-INTERFACE-7V): `categorizeError` rethrows a plain object carrying
 * `statusCode` (NOT `status`, and NOT an HttpErrorResponse instance). That object
 * then lands in `metadata.error` of the SchemaErrorService log entry, so without
 * the `.statusCode` read a handled 403 would not be recognized and would leak.
 */
function statusOf(value: unknown): number | null {
  if (value instanceof HttpErrorResponse) {
    return typeof value.status === 'number' ? value.status : null;
  }
  if (value && typeof value === 'object') {
    const s = (value as { status?: unknown }).status;
    if (typeof s === 'number') return s;
    const sc = (value as { statusCode?: unknown }).statusCode;
    if (typeof sc === 'number') return sc;
  }
  return null;
}

/** True for an opaque browser WebSocket onerror Event: `{ isTrusted: true }`, no close code. */
function isOpaqueWsEvent(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const v = value as { isTrusted?: unknown; code?: unknown };
  // A real CloseEvent carries a numeric `code`; an opaque onerror Event does not.
  return v.isTrusted === true && typeof v.code !== 'number';
}

/**
 * Layer-1 Sentry noise filter (ADMIN-INTERFACE-61/73/74/75/76).
 *
 * Returns true ONLY for two whitelisted, expected/handled families that must NOT
 * page Sentry, and NEVER for 5xx / network / unhandled errors:
 *   1. Handled auth: a 401/403 HttpErrorResponse (anywhere in metadata/args), or a
 *      numeric statusCode/status of 401/403, or an `[AuthError]` log whose
 *      metadata.category is 'authorization' (AuthErrorCategory.AUTHORIZATION).
 *   2. Opaque WebSocket: the browser onerror Event (`{ isTrusted: true }`, no code),
 *      or a known reconnect-attempt log ("WebSocket error on …" / "Schema WebSocket
 *      error" with an `attempt` field).
 *
 * Runs on the RAW LogEntry (before scrubObject rewrites metadata.error and loses
 * `.status`). Every property read is typeof/instanceof-guarded so a 500
 * HttpErrorResponse, a network ProgressEvent, a generic Error, a 404, or a terminal
 * "failed after max retries" message all fall through to false.
 */
export function isExpectedHandledNoise(entry: {
  level: LogLevel;
  message: string;
  metadata?: unknown;
  args?: unknown[];
}): boolean {
  const message = typeof entry.message === 'string' ? entry.message : '';
  const metadata = (entry.metadata && typeof entry.metadata === 'object')
    ? entry.metadata as Record<string, unknown>
    : undefined;

  // Candidate error-bearing values: metadata.error, metadata.event, each arg,
  // plus the metadata object itself (covers the flat [AuthError] logData case).
  const candidates: unknown[] = [];
  if (metadata) {
    candidates.push(metadata, metadata['error'], metadata['event']);
  }
  if (Array.isArray(entry.args)) {
    candidates.push(...entry.args);
  }

  // --- Handled-auth branch (401/403 only) ---
  for (const c of candidates) {
    const s = statusOf(c);
    if (s === 401 || s === 403) return true;
  }
  if (metadata) {
    const sc = metadata['statusCode'];
    if (sc === 401 || sc === 403) return true;
    if (message.startsWith('[AuthError]') && metadata['category'] === 'authorization') {
      return true;
    }
  }

  // --- Opaque-WS / reconnect branch ---
  if (/WebSocket error on /i.test(message) || /Schema WebSocket error/i.test(message)) {
    return true;
  }
  if (metadata) {
    if (isOpaqueWsEvent(metadata['event'])) return true;
    // A per-attempt reconnect log: WS error message + an `attempt` counter.
    if (message.toLowerCase().includes('websocket error') && metadata['attempt'] !== undefined) {
      return true;
    }
    // ADMIN-INTERFACE-80: a transient WS onerror surfaced as a 'WebSocket error'
    // log whose only metadata is the generic browser onerror string 'Connection
    // error' (no actionable status, no event/attempt). It's an expected/handled
    // reconnect condition already shown to the operator via a toast.
    if (message.toLowerCase().includes('websocket error') && metadata['error'] === 'Connection error') {
      return true;
    }
  }

  return false;
}

/**
 * SENTRY-NOISE-CLUSTER — Layer-1 transient-network filter for the logger bridge.
 *
 * Returns true ONLY for a status-0 HttpErrorResponse (a transport-layer failure:
 * offline, DNS, flaky Wi-Fi, opaque CORS). These are client-side conditions, NOT
 * application faults, and are already handled elsewhere: retryInterceptor retries
 * idempotent GET/HEAD on status 0, and callers surface their own UX. A persistent
 * status-0 means the browser cannot reach the backend — not a server fault to page on.
 *
 * WHY a SEPARATE predicate from `isExpectedHandledNoise` (which deliberately KEEPS
 * status-0 — see sentry-noise-filter.spec): the SDK's `Sentry.init({ ignoreErrors })`
 * ALREADY lists the browser's native network messages ('Failed to fetch', 'Load
 * failed', 'Network request failed'). But the logger bridge forwards errors as
 * `captureException(new Error(entry.message))`, where `entry.message` is our own
 * label (e.g. '[NetworkError] NETWORK_ERROR (0)') — so the SDK's network ignore list
 * never matches, and the network error bypasses it. This predicate closes that bypass
 * at the bridge, keeping the two concerns (handled-auth/WS vs. transient-network)
 * independently testable. It matches status 0 ONLY — 5xx, real 4xx, and unhandled
 * (statusless) JS errors always fall through to false and still reach Sentry.
 */
export function isTransientNetworkNoise(entry: {
  level: LogLevel;
  message: string;
  metadata?: unknown;
  args?: unknown[];
}): boolean {
  const metadata = (entry.metadata && typeof entry.metadata === 'object')
    ? entry.metadata as Record<string, unknown>
    : undefined;

  // Gather the same error-bearing candidates isExpectedHandledNoise inspects, so
  // an HttpErrorResponse carried at metadata, metadata.error, metadata.event, or in
  // args is recognized regardless of how the caller shaped its log.
  const candidates: unknown[] = [];
  if (metadata) {
    candidates.push(metadata, metadata['error'], metadata['event']);
  }
  if (Array.isArray(entry.args)) {
    candidates.push(...entry.args);
  }

  for (const c of candidates) {
    if (statusOf(c) === 0) return true;
  }
  // The auth-error reporter logs a flat `{ statusCode: 0, … }` for NETWORK_ERROR.
  if (metadata && metadata['statusCode'] === 0) return true;

  return false;
}

/**
 * Initialize Sentry SDK
 * Must be called before Angular bootstrap in main.ts
 */
export function initializeSentry(): void {
  const config = getSentryConfig();

  if (!config.enabled || !config.dsn) {
    if (!isProductionLike()) {
      // eslint-disable-next-line no-console
      console.info('[Sentry] Disabled - no DSN configured or explicitly disabled');
    }
    return;
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,

    // SECURITY: Disable automatic PII collection (IP addresses, cookies)
    // GDPR compliance: PII collection disabled by default; enable only after implementing consent flow
    sendDefaultPii: false,

    // Performance Monitoring
    tracesSampleRate: config.tracesSampleRate,

    // Error-event sampling — throttles error/message ingest so a burst of
    // client errors degrades gracefully instead of overrunning the ingest
    // quota (HTTP 429). 100% (the SDK default) was over-sending.
    sampleRate: config.sampleRate,

    // Session Replay (disabled by default for performance/privacy)
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Collapse identical consecutive errors into a single event so a tight
      // error loop doesn't fan out into hundreds of ingest requests.
      Sentry.dedupeIntegration(),
    ],

    // Filter out noisy or expected errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Filter out expected authentication errors (handled by auth service)
      if (error instanceof Error) {
        const message = error.message.toLowerCase();
        if (
          message.includes('token expired') ||
          message.includes('not authenticated') ||
          message.includes('session expired')
        ) {
          return null;
        }
      }

      // Defense-in-depth for the ErrorHandler path: a raw 401/403 HttpErrorResponse
      // that is actually THROWN (so it reaches the global handler) is expected/handled
      // noise. Narrowly scoped to 401/403 instanceof HttpErrorResponse so 5xx and
      // network errors are always kept. NOTE: this does NOT replace the handler-level
      // filter — logger-forwarded events have an empty hint.originalException (they are
      // synthesized via new Error(entry.message)), so only isExpectedHandledNoise catches
      // those; this only handles the rarer raw-throw case.
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        return null;
      }

      // Scrub sensitive data from request headers (case-insensitive per RFC 7230)
      if (event.request?.headers) {
        const sensitiveHeaders = ['authorization', 'x-api-key', 'cookie', 'x-csrf-token'];
        for (const headerKey of Object.keys(event.request.headers)) {
          if (sensitiveHeaders.includes(headerKey.toLowerCase())) {
            delete event.request.headers[headerKey];
          }
        }
      }

      // Scrub sensitive data from request body
      if (event.request?.data && typeof event.request.data === 'string') {
        try {
          const body = JSON.parse(event.request.data);
          event.request.data = JSON.stringify(scrubObject(body));
        } catch {
          // Not JSON, check for sensitive patterns in raw string
          if (typeof event.request.data === 'string' && containsSensitiveValue(event.request.data)) {
            event.request.data = '[REDACTED]';
          }
        }
      }

      // Redact sensitive query AND fragment params from the request URL.
      // Uses the shared sanitizeUrl utility (canonical 51-key set + fragment
      // parsing) — keeps coverage in lockstep with the rest of the codebase
      // instead of relying on the narrower local isSensitiveKey heuristic.
      if (event.request?.url) {
        event.request.url = sanitizeUrl(event.request.url);
      }

      // Scrub sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data) {
            breadcrumb.data = scrubObject(breadcrumb.data as Record<string, unknown>);
          }
          return breadcrumb;
        });
      }

      // Scrub sensitive data from extra context
      if (event.extra) {
        event.extra = scrubObject(event.extra as Record<string, unknown>);
      }

      // Scrub error message values for embedded PII
      if (event.exception?.values) {
        for (const exVal of event.exception.values) {
          if (exVal.value && containsSensitiveValue(exVal.value)) {
            exVal.value = '[REDACTED - contained sensitive data]';
          }
        }
      }

      return event;
    },

    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.teletrax.net',
      'jigsaw is not defined',
      // Network errors (expected in offline scenarios)
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // Angular router cancellation (not an error)
      'NavigationCancel',
    ],

    // Ignore specific URLs
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Safari extensions
      /^safari-extension:\/\//i,
    ],
  });

  if (!isProductionLike()) {
    // eslint-disable-next-line no-console
    console.info(`[Sentry] Initialized - environment: ${config.environment}, sample rate: ${config.tracesSampleRate}`);
  }
}

/**
 * Create external handler for LoggerService integration
 * This bridges the existing LoggerService to Sentry
 */
export function createSentryLogHandler(): (entry: LogEntry) => void {
  const config = getSentryConfig();

  if (!config.enabled || !config.dsn) {
    return () => {}; // No-op if Sentry is disabled
  }

  return (entry: LogEntry) => {
    // Layer-1 chokepoint (ADMIN-INTERFACE-61/73/74/75/76): drop handled 401/403 +
    // opaque-WS/reconnect logs at EVERY level (error AND warn), regardless of the
    // environment's logging threshold — so staging's warn-passthrough no longer
    // leaks them. MUST run on the RAW entry, before scrubObject rewrites
    // metadata.error into a redacted shape and loses the .status field.
    //
    // SENTRY-NOISE-CLUSTER: also drop status-0 transport failures here — the
    // bridge synthesizes `new Error(entry.message)`, so the SDK's `ignoreErrors`
    // network list can never match a network error forwarded this way. Both
    // predicates keep 5xx / real 4xx / unhandled errors flowing to Sentry.
    if (isExpectedHandledNoise(entry) || isTransientNetworkNoise(entry)) {
      return;
    }

    // Safely access metadata as record for property access
    const metadata = entry.metadata as Record<string, unknown> | undefined;
    // Scrub metadata before sending to Sentry
    const scrubbedMetadata = metadata ? scrubObject(metadata) : {};
    // Scrub args — can contain arbitrary objects including PII
    const scrubbedArgs = entry.args ? scrubArray(entry.args, 0, new WeakSet()) : undefined;

    // Only send errors and warnings to Sentry
    if (entry.level === 'error') {
      Sentry.withScope(scope => {
        scope.setLevel('error');
        scope.setExtras({
          ...scrubbedMetadata,
          args: scrubbedArgs,
        });
        if (scrubbedMetadata['correlationId'] && typeof scrubbedMetadata['correlationId'] === 'string') {
          scope.setTag('correlationId', scrubbedMetadata['correlationId']);
        }
        if (scrubbedMetadata['component'] && typeof scrubbedMetadata['component'] === 'string') {
          scope.setTag('component', scrubbedMetadata['component']);
        }
        if (scrubbedMetadata['module'] && typeof scrubbedMetadata['module'] === 'string') {
          scope.setTag('module', scrubbedMetadata['module']);
        }
        Sentry.captureException(new Error(entry.message));
      });
    } else if (entry.level === 'warn') {
      Sentry.withScope(scope => {
        scope.setLevel('warning');
        scope.setExtras(scrubbedMetadata);
        if (scrubbedMetadata['component'] && typeof scrubbedMetadata['component'] === 'string') {
          scope.setTag('component', scrubbedMetadata['component']);
        }
        if (scrubbedMetadata['module'] && typeof scrubbedMetadata['module'] === 'string') {
          scope.setTag('module', scrubbedMetadata['module']);
        }
        Sentry.captureMessage(entry.message);
      });
    }
  };
}

/**
 * Global error handler that captures unhandled exceptions to Sentry.
 *
 * Uses lazy Router injection to avoid circular dependency issues during
 * Angular bootstrap when errors occur before the Router is fully initialized.
 */
@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);

  handleError(error: unknown): void {
    // Only log to console in non-production-like environments to prevent
    // leaking error details (stack traces, internal paths) to browser DevTools.
    // Staging is treated as production-like per mock-flags.ts precedent.
    if (!isProductionLike()) {
      // eslint-disable-next-line no-console
      console.error('[SentryErrorHandler] Unhandled error:', error);
    }

    const config = getSentryConfig();
    if (!config.enabled || !config.dsn) {
      // Even in production, if Sentry is disabled, log a minimal notice
      // so errors don't vanish silently with zero observability
      if (isProductionLike()) {
        // eslint-disable-next-line no-console
        console.warn('[ErrorHandler] Error occurred');
      }
      return;
    }

    // Extract error details (depth-limited to prevent stack overflow)
    const extractedError = this.extractError(error);

    // Add router context with lazy injection (Router may not be available at bootstrap)
    Sentry.withScope(scope => {
      // Safely get router URL - may fail during bootstrap
      try {
        const router = this.injector.get(Router, null);
        if (router) {
          scope.setExtra('route', sanitizeUrl(router.url));
        }
      } catch {
        // Router not available, skip route context
        scope.setExtra('route', 'unknown (bootstrap error)');
      }

      // Sanitize error type to avoid leaking internal class names to Sentry
      scope.setExtra('errorType', sanitizeErrorType(error));
      scope.setExtra('timestamp', new Date().toISOString());

      if (extractedError instanceof Error) {
        Sentry.captureException(extractedError);
      } else {
        Sentry.captureMessage(String(extractedError), 'error');
      }
    });
  }

  /**
   * Extract the actual error from Angular's wrapped error objects.
   * Angular wraps errors in various containers depending on context
   * (Zone.js rejections, HttpErrorResponse, etc.)
   *
   * Depth-limited to prevent stack overflow from circular references.
   */
  extractError(error: unknown, depth = 0): Error | string {
    if (depth >= 10) {
      return error instanceof Error ? error : String(error ?? 'Unknown error (max depth)');
    }

    // Handle Angular's wrapped errors
    if (error && typeof error === 'object') {
      // Promise rejection wrapper
      if ('rejection' in error) {
        return this.extractError((error as { rejection: unknown }).rejection, depth + 1);
      }
      // Angular's ErrorEvent wrapper
      if ('originalError' in error) {
        return this.extractError((error as { originalError: unknown }).originalError, depth + 1);
      }
      // HttpErrorResponse or generic error wrapper
      if ('error' in error) {
        return this.extractError((error as { error: unknown }).error, depth + 1);
      }
      // Direct Error instance
      if (error instanceof Error) {
        return error;
      }
    }

    return error instanceof Error ? error : String(error);
  }
}

/**
 * Hash a string using Web Crypto API (SHA-256).
 * Falls back to djb2 only when crypto.subtle is unavailable.
 * The result is a non-reversible identifier safe for third-party services.
 */
async function hashIdentifier(value: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 16);
  }
  // Fallback: simple string hash (djb2 algorithm)
  return hashIdentifierFallback(value);
}

/**
 * Fallback hash using djb2 algorithm.
 * Used only when Web Crypto API is unavailable (e.g., non-secure contexts).
 */
function hashIdentifierFallback(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash) + value.charCodeAt(i);
  }
  return Math.abs(hash).toString(16).padStart(16, '0').substring(0, 16);
}

/**
 * Service for programmatic Sentry interaction.
 *
 * Provides methods to set user context, add breadcrumbs, capture exceptions,
 * and manage tags/context. All PII is hashed before being sent to Sentry
 * to comply with GDPR requirements. All data passed to Sentry is scrubbed
 * through the centralized scrubObject utility.
 */
@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private readonly config = getSentryConfig();

  /**
   * Check if Sentry is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled && !!this.config.dsn;
  }

  /**
   * Set user context for error tracking.
   * SECURITY: User ID is hashed (SHA-256 preferred, djb2 fallback) before
   * sending to Sentry. Email and username are NOT sent to comply with GDPR.
   *
   * Prefer setUserAsync() when you can await — it uses SHA-256 via Web Crypto.
   *
   * @param user - User object with id (email/username ignored for privacy)
   */
  setUser(user: { id: string; email?: string; username?: string } | null): void {
    if (!this.isEnabled()) return;

    if (user) {
      // SECURITY: Hash user ID to prevent PII exposure to third-party.
      // Uses djb2 fallback synchronously; prefer setUserAsync for SHA-256.
      Sentry.setUser({
        id: hashIdentifierFallback(user.id),
        // email: REMOVED - GDPR compliance
        // username: REMOVED - GDPR compliance
      });
    } else {
      Sentry.setUser(null);
    }
  }

  /**
   * Set user context with async hashing (SHA-256 via Web Crypto).
   * Preferred over setUser() when you can await the result.
   */
  async setUserAsync(user: { id: string } | null): Promise<void> {
    if (!this.isEnabled()) return;

    if (user) {
      const hashedId = await hashIdentifier(user.id);
      Sentry.setUser({ id: hashedId });
    } else {
      Sentry.setUser(null);
    }
  }

  /**
   * Add a breadcrumb for debugging context.
   * Data is scrubbed for PII before sending to Sentry.
   */
  addBreadcrumb(
    message: string,
    category: string,
    data?: Record<string, unknown>,
    level: Sentry.SeverityLevel = 'info'
  ): void {
    if (!this.isEnabled()) return;

    Sentry.addBreadcrumb({
      message,
      category,
      data: data ? scrubObject(data) : undefined,
      level,
      timestamp: Date.now() / 1000,
    });
  }

  /**
   * Capture an exception.
   * Context is scrubbed for PII before sending to Sentry.
   */
  captureException(error: Error, context?: Record<string, unknown>): string | undefined {
    if (!this.isEnabled()) return undefined;

    return Sentry.captureException(error, {
      extra: context ? scrubObject(context) : undefined,
    });
  }

  /**
   * Capture a message
   */
  captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): string | undefined {
    if (!this.isEnabled()) return undefined;

    return Sentry.captureMessage(message, level);
  }

  /**
   * Set extra context for subsequent errors.
   * Context is scrubbed for PII before sending to Sentry.
   */
  setContext(name: string, context: Record<string, unknown>): void {
    if (!this.isEnabled()) return;

    Sentry.setContext(name, scrubObject(context));
  }

  /**
   * Set a tag for filtering.
   * Note: For potentially sensitive identifiers, use setHashedTag instead.
   */
  setTag(key: string, value: string): void {
    if (!this.isEnabled()) return;

    Sentry.setTag(key, value);
  }

  /**
   * Set a tag with a hashed value for filtering.
   * Use this for potentially sensitive identifiers like tenant IDs.
   */
  setHashedTag(key: string, value: string): void {
    if (!this.isEnabled()) return;

    Sentry.setTag(key, hashIdentifierFallback(value));
  }
}
